import { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Wifi,
  AlertCircle,
  AlertTriangle,
  Clock,
  RefreshCw,
  Unlink,
  Loader2,
  HardDrive,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Header } from '@/components/Header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { Station, StationReport, StationLog, StationTask } from '@/api/legacy';
import { useQueryClient } from '@tanstack/react-query';
import {
  useStationDetails,
  useClearPorts,
  useUnassignStation,
} from '@/features/legacy/hooks/useStations';

interface StationWithSobid extends Station {
  displayName: string;
  sobid: number;
}

function timeSince(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds} сек. назад`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} мин. назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч. назад`;
  const days = Math.floor(hours / 24);
  return `${days} дн. назад`;
}

function formatDateTime(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleString('ru-RU');
}

function Section({
  title,
  badge,
  children,
  defaultOpen = false,
}: {
  title: string;
  badge?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full p-2 bg-muted rounded-lg hover:bg-muted/80">
        <span className="font-medium text-sm flex items-center gap-2">
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {title}
        </span>
        {badge && (
          <Badge variant="secondary" className="text-xs">
            {badge}
          </Badge>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2 space-y-2">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

function ReportItem({ report, isFirst }: { report: StationReport; isFirst: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const date = new Date(report.created * 1000);
  const errorLines = report.errors?.trim().split('\r\n').filter(e => e) || [];
  const hdds = report.hdd?.split('-=-=-=-').filter(h => h.trim()) || [];

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className={`w-full text-left p-2 rounded border ${isFirst ? 'bg-card' : 'bg-muted/30'}`}>
        <div className="flex items-center justify-between">
          <span className={`text-xs ${isFirst ? '' : 'text-muted-foreground'}`}>
            {timeSince(date)} ({formatDateTime(report.created)})
          </span>
          {errorLines.length > 0 && (
            <Badge variant="destructive" className="text-xs">
              {errorLines.length} ошибок
            </Badge>
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="p-2 text-xs space-y-2">
        {report.pwd && <p className="text-muted-foreground">{report.pwd}</p>}
        {errorLines.length > 0 && (
          <ol className="list-decimal list-inside text-red-600 space-y-1">
            {errorLines.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ol>
        )}
        {hdds.map((hdd, i) => {
          const lines = hdd.trim().split('\n');
          const title = lines[0];
          const age = lines[lines.length - 1];
          const ageYears = !isNaN(Number(age)) ? Math.round((Number(age) / 24 / 365) * 10) / 10 : null;
          const hasFailed = hdd.includes('FAILED!') || hdd.includes('rror');

          return (
            <div key={i} className={`p-2 rounded ${hasFailed ? 'bg-red-50 border border-red-200' : 'bg-muted/50'}`}>
              <div className="flex items-center gap-1 font-medium">
                <HardDrive className="h-3 w-3" />
                <span className={hasFailed ? 'text-red-600' : ''}>
                  {title} {ageYears !== null && `(${ageYears} лет)`}
                </span>
              </div>
            </div>
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
}

function LogItem({ log, isFirst }: { log: StationLog; isFirst: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const errorLines = log.errors?.trim().split('\r\n').filter(e => e) || [];

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className={`w-full text-left p-2 rounded border ${isFirst ? 'bg-card' : 'bg-muted/30'}`}>
        <div className="flex items-center justify-between">
          <span className={`text-xs ${isFirst ? '' : 'text-muted-foreground'}`}>
            {log.date}
          </span>
          {errorLines.length > 0 && (
            <Badge variant="destructive" className="text-xs">
              {errorLines.length} ошибок
            </Badge>
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="p-2 text-xs space-y-2">
        {errorLines.length > 0 && (
          <div className="text-red-600 whitespace-pre-wrap">{log.errors}</div>
        )}
        {log.log && (
          <div className="text-muted-foreground whitespace-pre-wrap max-h-40 overflow-y-auto">
            {log.log}
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

function TaskItem({ task, isFirst }: { task: StationTask; isFirst: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const lastCopyDate = new Date(task.lastCbuFileDate);
  const nextCopyDate = new Date(task.lastCbuScheduleDate);
  const isOverdue = nextCopyDate < new Date();
  const errorLines = task.errors?.trim().split('\r\n').filter(e => e) || [];

  let sources: string[] = [];
  let destinations: Record<string, unknown> = {};
  try {
    sources = JSON.parse(task.sources) || [];
  } catch { /* ignore */ }
  try {
    destinations = JSON.parse(task.destinations) || {};
  } catch { /* ignore */ }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className={`w-full text-left p-2 rounded border ${isFirst ? 'bg-card' : 'bg-muted/30'}`}>
        <div className="flex items-center justify-between">
          <span className={`text-xs font-medium ${isFirst ? '' : 'text-muted-foreground'}`}>
            [{task.name}] {timeSince(lastCopyDate)}
          </span>
          <div className="flex gap-1">
            {isOverdue && isFirst && (
              <Badge variant="destructive" className="text-xs">просрочено</Badge>
            )}
            {errorLines.length > 0 && (
              <Badge variant="outline" className="text-xs bg-red-50 text-red-700">
                {errorLines.length} ошибок
              </Badge>
            )}
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="p-2 text-xs space-y-1">
        <p><strong>Откуда:</strong> {sources.length > 0 ? `[${sources.join('], [')}]` : 'не определено'}</p>
        <p><strong>Куда:</strong> {Object.keys(destinations).map(d => `[${d}]`).join(' ') || 'не определено'}</p>
        <p>
          <strong>Прошлая копия:</strong> {timeSince(lastCopyDate)} ({task.lastCbuFileDate})
        </p>
        <p className={isOverdue && isFirst ? 'text-red-600 font-medium' : ''}>
          <strong>Следующая копия:</strong> {timeSince(nextCopyDate)} ({task.lastCbuScheduleDate})
        </p>
        <p><strong>Копий:</strong> {task.copies}</p>
        {errorLines.length > 0 && (
          <div className="mt-2 p-2 bg-red-50 rounded">
            <strong className="text-red-700">Ошибки:</strong>
            <ol className="list-decimal list-inside text-red-600 mt-1">
              {errorLines.map((err, i) => (
                <li key={i}>{err.replace(/@~~@/g, '\n')}</li>
              ))}
            </ol>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function StationDetailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const station = location.state?.station as StationWithSobid | undefined;

  const [showUnassign, setShowUnassign] = useState(false);
  const [unassignComment, setUnassignComment] = useState('');

  const queryClient = useQueryClient();
  const clearPorts = useClearPorts(station?.sobid);
  const unassignStation = useUnassignStation(station?.sobid);

  const { data: details, isLoading: detailsLoading, refetch, isRefetching } = useStationDetails(
    station?.sobid,
    station?.displayName ?? null
  );

  const lastReportDate = station?.lastreport ? new Date(station.lastreport) : null;
  const daysSinceReport = lastReportDate
    ? Math.floor((Date.now() - lastReportDate.getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const isStale = daysSinceReport !== null && daysSinceReport > 3;

  const briefErrorLines = useMemo(() => {
    if (!station?.errors) return [];
    return station.errors.split('\r\n').filter((e) => e.trim());
  }, [station?.errors]);

  const ports = useMemo(() => {
    if (!station?.ports) return [];
    if (station.ports.includes('Error')) return [];
    return station.ports.split(',').filter((p) => p.trim());
  }, [station?.ports]);

  const hasPortError = station?.ports?.includes('Error');

  const reportErrors = details?.reports?.[0]?.errors?.trim().split('\r\n').filter(e => e).length || 0;
  const logErrors = details?.logs?.[0]?.errors?.trim().split('\r\n').filter(e => e).length || 0;
  const taskErrors = details?.tasks?.[0]?.errors?.trim().split('\r\n').filter(e => e).length || 0;

  const handleUnassignConfirm = () => {
    if (station) {
      unassignStation.mutate(
        { stationId: station.id, comment: unassignComment },
        { onSuccess: () => navigate(-1) }
      );
    }
  };

  if (!station) {
    return (
      <div className="flex flex-col min-h-full">
        <Header title="Станция" showBack />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">Станция не найдена</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full">
      <Header
        title={station.displayName}
        subtitle={lastReportDate
          ? `Последний отчёт: ${lastReportDate.toLocaleString('ru-RU')}`
          : 'Нет отчётов'}
        showBack
        rightContent={
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              refetch();
              queryClient.invalidateQueries({ queryKey: ['legacy', 'allErrors'] });
            }}
            disabled={isRefetching}
          >
            <RefreshCw className={`h-5 w-5 ${isRefetching ? 'animate-spin' : ''}`} />
          </Button>
        }
      />

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Status badges */}
        <div className="flex flex-wrap gap-2">
          {isStale && daysSinceReport !== null && (
            <Badge variant="destructive">
              <Clock className="h-3 w-3 mr-1" />
              Нет данных {daysSinceReport} дней
            </Badge>
          )}
          {station.critical && (
            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {station.critical.substring(0, 50)}
            </Badge>
          )}
        </div>

        {/* Ports section */}
        <div className="p-3 bg-muted rounded-lg">
          <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
            <Wifi className="h-4 w-4" />
            Открытые порты
          </h4>
          {hasPortError ? (
            <p className="text-sm text-yellow-700">{station.ports}</p>
          ) : ports.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {ports.map((port, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs font-mono">
                  {port}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Не обнаружено открытых портов</p>
          )}
          {station.portcheck && (
            <p className="text-xs text-muted-foreground mt-2">
              Дата опроса: {station.portcheck}
            </p>
          )}
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => clearPorts.mutate({ stationId: station.id })}
            disabled={clearPorts.isPending}
          >
            {clearPorts.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-1" />
            )}
            Обновить данные о портах
          </Button>
        </div>

        {/* Loading detailed data */}
        {detailsLoading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Загрузка подробностей...</span>
          </div>
        )}

        {/* Detailed sections */}
        {details && (
          <>
            <Section
              title="Отчеты"
              badge={reportErrors > 0 ? `${reportErrors} ошибок` : undefined}
              defaultOpen={reportErrors > 0}
            >
              {details.reports.length === 0 ? (
                <p className="text-sm text-muted-foreground p-2">Нет отчётов</p>
              ) : (
                details.reports.map((report, idx) => (
                  <ReportItem key={idx} report={report} isFirst={idx === 0} />
                ))
              )}
            </Section>

            <Section
              title="Логи"
              badge={logErrors > 0 ? `${logErrors} ошибок` : undefined}
            >
              {details.logs.length === 0 ? (
                <p className="text-sm text-muted-foreground p-2">Нет логов за последние 15 дней</p>
              ) : (
                details.logs.map((log, idx) => (
                  <LogItem key={idx} log={log} isFirst={idx === 0} />
                ))
              )}
            </Section>

            <Section
              title="Задачи"
              badge={taskErrors > 0 ? `${taskErrors} ошибок` : undefined}
            >
              {details.tasks.length === 0 ? (
                <p className="text-sm text-muted-foreground p-2">Нет задач резервного копирования</p>
              ) : (
                details.tasks.map((task, idx) => (
                  <TaskItem key={idx} task={task} isFirst={idx === 0} />
                ))
              )}
            </Section>
          </>
        )}

        {/* Brief errors (when no detailed data) */}
        {!details && !detailsLoading && briefErrorLines.length > 0 && (
          <div className="p-3 bg-red-50 rounded-lg border border-red-200">
            <h4 className="text-sm font-medium mb-2 flex items-center gap-2 text-red-700">
              <AlertCircle className="h-4 w-4" />
              Ошибки ({briefErrorLines.length})
            </h4>
            <ol className="text-xs space-y-1 list-decimal list-inside text-red-700">
              {briefErrorLines.slice(0, 10).map((error, idx) => (
                <li key={idx} className="break-words">
                  {error.length > 200 ? error.substring(0, 200) + '...' : error}
                </li>
              ))}
              {briefErrorLines.length > 10 && (
                <li className="text-muted-foreground">...и ещё {briefErrorLines.length - 10}</li>
              )}
            </ol>
          </div>
        )}

        {/* Unassign section */}
        <div className="pt-2 border-t">
          {!showUnassign ? (
            <Button
              variant="outline"
              size="sm"
              className="w-full text-red-600 border-red-300 hover:bg-red-50"
              onClick={() => setShowUnassign(true)}
            >
              <Unlink className="h-4 w-4 mr-1" />
              Отвязать станцию
            </Button>
          ) : (
            <div className="space-y-2">
              <Input
                placeholder="Причина отвязки..."
                value={unassignComment}
                onChange={(e) => setUnassignComment(e.target.value)}
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    setShowUnassign(false);
                    setUnassignComment('');
                  }}
                >
                  Отмена
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  className="flex-1"
                  onClick={handleUnassignConfirm}
                  disabled={unassignStation.isPending}
                >
                  {unassignStation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Unlink className="h-4 w-4 mr-1" />
                  )}
                  Отвязать
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
